import { getValueWithPlaceholder, Login, LoginUiType, Passkey, Unprotect } from 'persistable-types';

import { normalizeWebsitePattern } from './normalizeLogin/normalizeWebsitePattern';

export interface ImplicitLoginWebsite {
  website: string;
  linkTarget?: string;
  /**
   * Further hosts the website also applies to, as bare hosts (including the port, if any).
   *
   * Only set for passkeys, where the relying party may declare related origins the passkey is valid
   * on as well. Consumers that match or search should treat these like the website itself, display
   * should present them the way related domains are presented for a normal website.
   */
  relatedOrigins?: string[];
}

/**
 * Only the plain passkey fields are required, so that logins with either protected or unprotected
 * secrets can be passed in.
 */
type LoginForImplicitWebsites = Partial<Pick<Unprotect<Login, unknown>, 'uiType'>> & {
  passkey?: Pick<Passkey, 'rpId' | 'relatedOrigins'>;
};

export default function getImplicitLoginWebsites(
  login: LoginForImplicitWebsites,
): ImplicitLoginWebsite[] {
  const result: ImplicitLoginWebsite[] = [];

  // A passkey can only be used on its relying party, which makes the rpId a website of the login.
  const rpId = login.passkey?.rpId.trim();
  if (login.passkey && rpId) {
    result.push({
      website: normalizeWebsitePattern(rpId),
      relatedOrigins: getRelatedOriginHosts(login.passkey.relatedOrigins, rpId),
      // WebsiteEdit otherwise links to http://<website>, and WebAuthn only works on secure origins.
      linkTarget: `https://${rpId}`,
    });
  }

  const uiType = getValueWithPlaceholder(login.uiType);
  if (uiType === LoginUiType.elsterCertificate) {
    result.push({
      website: 'elster.de',
      linkTarget: 'https://www.elster.de/eportal/login/softpse',
    });
  }

  return result;
}

/**
 * Reduces the passkey's related origins to the hosts they apply to.
 *
 * Note that this is deliberately more permissive than the related origins validation procedure used
 * to authorize a passkey operation: we only compare hosts, ignoring the scheme and the limit on
 * distinct registrable labels. Being too permissive here only offers a login on a page where it
 * likely belongs, while the strict check stays the authority for GET and CREATE requests.
 */
function getRelatedOriginHosts(relatedOrigins: string[] | undefined, rpId: string) {
  if (!relatedOrigins?.length) {
    return undefined;
  }

  const hosts = new Set<string>();
  for (const relatedOrigin of relatedOrigins) {
    let host: string;
    try {
      ({ host } = new URL(relatedOrigin));
    } catch {
      // Faulty entry, the relying party controls this list and we never validated its contents.
      continue;
    }
    // The rpId is the website of this entry, no need to repeat it.
    if (!host || host === rpId) {
      continue;
    }
    hosts.add(host);
  }

  return hosts.size ? [...hosts] : undefined;
}
